'use client';

import React, { useRef } from 'react';
import { usePathname } from 'next/navigation';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useTranslations } from 'next-intl';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { runScrollTriggerSetup } from 'utils/performanceLabScrollTrigger';
import { PLCTA } from './atoms';
import {
  FixedBgContent,
  FixedBgImageWrap,
  FixedBgLayer,
  FixedBgSectionRoot,
  FixedBgViewportContent,
} from './fixedBackgroundScroll';
import { PINCODE_PARALLAX_BG } from './plLandingAssets';
import { ResponsiveParallaxBackgroundImages } from './ResponsiveParallaxBackgroundImages';
import { useHandleBookNow } from './hooks';

import styles from './PincodeCtaBannerSection.module.css';

gsap.registerPlugin(ScrollTrigger);
const BG_FALLBACK = '#737373';
const SECTION_VH_MOBILE = 1;
const SECTION_VH_DESKTOP = 0.8;

export const PincodeCtaBannerSection: React.FC<{
  locationLabel?: string;
  ctaLabel?: string;
  ctaAriaLabel?: string;
  onCtaClick?: () => void;
}> = ({ locationLabel, ctaLabel: ctaLabelProp, ctaAriaLabel, onCtaClick }) => {
  const t = useTranslations('performance-lab');
  const pathname = usePathname();
  const isRecovery = pathname?.includes('/recovery');
  const handleBookNow = useHandleBookNow({
    category: isRecovery ? 'recovery' : 'diagnostics',
  });
  const handleCtaClick = onCtaClick ?? handleBookNow;
  const ctaLabel = ctaLabelProp ?? t('pincodeCtaBanner.cta');
  const sectionRef = useRef<HTMLElement>(null);
  const bgLayerRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) return;

    return runScrollTriggerSetup({
      debugLabel: 'PincodeCtaBannerSection parallax',
      isReady: () =>
        Boolean(sectionRef.current) && Boolean(bgLayerRef.current),
      setup: () => {
        const section = sectionRef.current;
        const bgLayer = bgLayerRef.current;
        if (!section || !bgLayer) return;

        const trigger = ScrollTrigger.create({
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const eased = Math.min(self.progress / 0.5, 1);
            const scale = 1 + eased * 0.12;
            const translateY = (eased - 0.5) * -30;
            bgLayer.style.transform = `scale(${scale}) translateY(${translateY}px)`;
          },
        });

        return {
          cleanup: () => {
            trigger.kill();
            bgLayer.style.transform = '';
          },
        };
      },
    });
  }, []);

  return (
    <FixedBgSectionRoot
      ref={sectionRef}
      role="region"
      aria-labelledby="pincode-cta-banner-title"
      $mobileVh={SECTION_VH_MOBILE}
      $desktopVh={SECTION_VH_DESKTOP}
      $fallbackBg={BG_FALLBACK}
    >
      <FixedBgLayer ref={bgLayerRef} aria-hidden>
        <FixedBgImageWrap $fallback={BG_FALLBACK}>
          <ResponsiveParallaxBackgroundImages src={PINCODE_PARALLAX_BG} />
        </FixedBgImageWrap>
      </FixedBgLayer>
      <FixedBgContent>
        <FixedBgViewportContent>
          <div className={styles.content}>
            <p className={styles.locationLabel}>
              {locationLabel ?? t('hero.locationLabel')}
            </p>
            <h2 className={styles.title} id="pincode-cta-banner-title">
              {t('pincodeCtaBanner.title')}
            </h2>
            <PLCTA
              className={styles.bannerCta}
              onClick={handleCtaClick}
              aria-label={ctaAriaLabel ?? t('pincodeCtaBanner.ctaAria')}
              data-buttontype="performance_lab_pincode_cta_banner"
            >
              {ctaLabel}
            </PLCTA>
          </div>
        </FixedBgViewportContent>
      </FixedBgContent>
    </FixedBgSectionRoot>
  );
};
